import SubjectIcon from "@/app/components/logos/SubjectIcon";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import { Chip, Group } from "@mantine/core";
import { Subject } from "@tsTypes/subject-schema";
import { useState } from "react";
import { useInputStyles } from "./FieldTextValue";
import chipClasses from "./FieldMultiChipValue.module.css";

type Props =
  | {
      subjectNames: Subject["Name"][];
      label: string;
      editing: boolean;
      formProps: any;
      fullWidth?: boolean;
      /** When true, label is not rendered (use with external FieldLabel). */
      hideLabel?: boolean;
    }
  | {
      subjectNames: Subject["Name"][];
      label: string;
      editing?: undefined;
      formProps?: undefined;
      fullWidth?: boolean;
      hideLabel?: boolean;
    };

export default function FieldMultiChipValue({
  subjectNames,
  editing,
  formProps,
  label,
  fullWidth,
  hideLabel = false,
}: Props) {
  const { classes } = useInputStyles();
  const [localValue, setLocalValue] = useState<string[]>([]);
  const {
    value: formValue,
    onChange: formOnChange,
    ...restFormProps
  } = formProps ?? {};
  const normalizedFormValue: string[] | undefined = Array.isArray(formValue)
    ? (formValue as string[])
    : typeof formValue === "string" && formValue
    ? [formValue]
    : undefined;
  const currentValue: string[] = normalizedFormValue ?? localValue;
  function handleChange(next: string[]) {
    if (!editing) {
      return;
    }
    if (typeof formOnChange === "function") {
      formOnChange(next);
    } else {
      setLocalValue(next);
    }
  }
  return (
    <Chip.Group
      multiple
      value={currentValue}
      onChange={handleChange}
      {...restFormProps}
    >
      {!hideLabel && <span className={classes.label}>{label}</span>}
      <Group pl={"sm"} w={fullWidth ? "100%" : undefined}>
        {subjectNames.map((subj) => {
          const subjectColorIndex = getSubjectColorIndex(subj);
          return (
            <Chip
              key={subjectColorIndex}
              radius={"sm"}
              defaultChecked={!formProps}
              value={subj}
              color={subjectColorIndex}
              lh={"0.9rem"}
              variant="filled"
              classNames={{
                root: editing
                  ? chipClasses.chipRootEditable
                  : chipClasses.chipRootReadOnly,
              }}
            >
              <Group wrap="nowrap" gap={"xs"}>
                <SubjectIcon
                  subjectColorIndex={subjectColorIndex}
                  size="sm"
                />
                {subj}
              </Group>
            </Chip>
          );
        })}
      </Group>
    </Chip.Group>
  );
}
